import type { Material } from '@babylonjs/core/Materials/material';

/**
 * Decals (logos, stickers, road paint) sit millimeters in front of their surface. Far away the
 * depth buffer cannot separate them any more and they flicker (z-fighting). Blender materials
 * named `…_DECAL` (or `…_DECAL2` for a decal lying on another decal) get a polygon offset that
 * pulls them towards the camera independently of the distance.
 */
const DECAL_NAME = /_DECAL(\d)?$/;
/** Polygon offset per decal level (factor scales with the surface slope, units with depth precision). */
const DECAL_OFFSET = { factor: -1, units: -4 } as const;

/** Decal level from a material name: 0 = no decal, 1 = `_DECAL`, n = `_DECALn`. */
export function decalLevel(materialName: string): number {
  const match = DECAL_NAME.exec(materialName);
  if (!match) return 0;
  return match[1] ? Number(match[1]) : 1;
}

export function applyDecalOffset(material: Material): void {
  const level = decalLevel(material.name);
  if (level > 0) applyDepthOffset(material, level);
  else if (isInsetSurface(material.name)) applyDepthOffset(material, INSET_LEVEL);
}

/**
 * Glass in its frame, lenses and LED panels in their housing, lamps, windows: modelled flush with (a millimetre in
 * front of) the surface around them – they flickered in the distance (user 2026-10-04: floodlights, crane cab glass).
 * Half a decal level: a sticker on the glass (…_DECAL) still lies in front of it. Not the beer bottle's glass – a solid
 * body with its label on it.
 */
const INSET_NAME = /_(GLASS|LENS|LED|LAMP|LIGHT|WINDOW)(_DECAL)?$/;
const INSET_LEVEL = 0.5;

export function isInsetSurface(materialName: string): boolean {
  return INSET_NAME.test(materialName) && !materialName.includes('BOTTLE');
}

/**
 * Semi-transparent surfaces that always stand behind the site fence (scaffold nets on the roadside houses): drawn
 * before the other transparent surfaces. Sorted by distance per mesh, the fence mesh and a net flipped their order while
 * running and the net blinked behind the fence.
 */
export const BEHIND_FENCE = /^MAT_ROADSIDE_NET$/;

/**
 * Polygon offset of `level` decal layers – for surfaces the game lays over a model itself (ad
 * planes on fences, walls, scaffold wraps). Level 1 wins against the coplanar surface behind it at
 * any distance; real occluders a few centimetres in front (pipes, posts) still cover it.
 */
export function applyDepthOffset(material: Material, level: number): void {
  material.zOffset = DECAL_OFFSET.factor * level;
  material.zOffsetUnits = DECAL_OFFSET.units * level;
}
